import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useGameStore } from "../../store/gameStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Eye, MessageCircleWarning } from "lucide-react";
import TriggerBadge from "./TriggerBadge";

export default function SightConeTriggerModal({ gameId, character, myMemberId, myMemberName }) {
  const pending = useGameStore((s) => s.pendingSightConeTrigger);
  const setPending = useGameStore((s) => s.setPendingSightConeTrigger);
  const sendWs = useGameStore((s) => s.sendWs);
  const [stats, setStats] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [talkedToGm, setTalkedToGm] = useState(false);

  const isMine = pending && pending.member_id === myMemberId;

  useEffect(() => {
    setAcknowledged(false);
    setTalkedToGm(false);
    if (isMine && character?.id) {
      api.get(`/characters/${character.id}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    }
  }, [pending?.npc_token_id]);

  if (!isMine) return null;

  const close = () => {
    setPending(null);
    setResult(null);
    setStats(null);
    setAcknowledged(false);
    setTalkedToGm(false);
  };

  const talkToGm = (rollInfo) => {
    sendWs({ type: "talk_to_gm_ping", data: { member_id: myMemberId, member_name: myMemberName, label: `${pending.npc_name} might spot you`, roll_result: rollInfo || null } });
    setTalkedToGm(true);
    toast.success("The GM has been notified — they'll narrate what happens next.");
  };

  const doRoll = async () => {
    if (!character?.id) {
      toast.error("Create a character first to roll");
      return;
    }
    setRolling(true);
    try {
      let total;
      const stealthMod = stats?.skills?.stealth?.modifier;
      if (typeof stealthMod === "number") {
        const { data } = await api.post(`/characters/${character.id}/roll`, { game_id: gameId, label: "Stealth", modifier: stealthMod });
        total = data.roll_breakdown.total;
      } else {
        const { data } = await api.post(`/characters/${character.id}/paper-roll`, { game_id: gameId, die: "1d20", bonuses: [], label: "Stealth" });
        total = data.roll_breakdown.total;
      }
      const passed = total >= pending.dc;
      const { data: resolveData } = await api.post(`/games/${gameId}/tokens/${pending.npc_token_id}/sight-cone-resolve`, { passed, player_token_id: pending.token_id });
      setResult({ total, pass: passed, combatStarted: resolveData?.combat_started });
      if (resolveData?.combat_started) toast.error(`${pending.npc_name} spots you! Combat begins!`);
    } catch {
      toast.error("Roll failed");
    } finally {
      setRolling(false);
    }
  };

  return (
    <>
      {!acknowledged && (
        <TriggerBadge testId="sight-cone-trigger-badge" label={pending.hunting ? "You're being watched!" : "In view"} onClick={() => setAcknowledged(true)} />
      )}
      <Dialog open={acknowledged} onOpenChange={(v) => !v && close()}>
        <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="sight-cone-trigger-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Eye className="w-5 h-5" /> {pending.hunting ? "You're being watched!" : "In view"}</DialogTitle>
            <DialogDescription>
              {pending.npc_name} might notice you — roll Stealth to beat DC {pending.dc}
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="space-y-3">
              <p className={`text-2xl font-display ${result.pass ? "text-[#4d7c0f]" : "text-[#D90429]"}`} data-testid="sight-cone-roll-result">
                Rolled {result.total} — {result.pass ? "Stayed hidden!" : "Spotted!"}
              </p>
              {result.combatStarted && (
                <p data-testid="sight-cone-combat-started" className="text-sm text-[#D90429]">{pending.npc_name} raises the alarm — roll for initiative!</p>
              )}
              <Button data-testid="sight-cone-close-button" onClick={close} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">Close</Button>
            </div>
          ) : talkedToGm ? (
            <div className="space-y-3 text-center py-2">
              <MessageCircleWarning className="w-8 h-8 text-[#D4AF37] mx-auto animate-pulse" />
              <p className="text-sm text-[#f1e0c5]/70">Waiting on the GM to narrate what happens…</p>
              <Button data-testid="sight-cone-close-button" onClick={close} variant="secondary" className="w-full rounded-full">Close</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {!character?.id && <p className="text-xs text-[#f1e0c5]/50">Create a character to roll for this check.</p>}
              <Button data-testid="sight-cone-roll-button" onClick={doRoll} disabled={rolling || !character?.id} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
                Roll Stealth
              </Button>
              <Button data-testid="sight-cone-talk-to-gm-button" onClick={() => talkToGm()} disabled={rolling} variant="secondary" className="w-full rounded-full gap-2">
                <MessageCircleWarning className="w-4 h-4" /> Talk to GM
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
